import React, { useEffect } from 'react'
import jwt_decode from 'jwt-decode'
import { useQuery } from '@apollo/client'

import { GET_KINDS, GET_VENDOR, GET_VENDOR_PUBLISHERS } from './queries'
import Storage from './Storage'
import {
  CIRCULATIONS_HOLDS_FILTERS_STORAGE_KEY,
  CIRCULATIONS_OVERVIEW_FILTERS_STORAGE_KEY,
  GLANCE_FILTERS_STORAGE_KEY,
  INVENTORY_FILTERS_STORAGE_KEY,
  SETTINGS_STORAGE_KEY,
  TITLE_CIRCULATIONS_FILTERS_STORAGE_KEY,
  TITLE_PERFORMANCE_FILTERS_STORAGE_KEY,
  TOKEN_STORAGE_KEY,
} from './constants'
import { client } from './api'
import {
  useSafeSetState,
  useStoredMethod,
  useStoredReducer,
} from '../components/hooks'
import { useAvailableCirculationMethods } from '../utils/bundleHelpers.tsx'
import { CIRCULATION_METHOD_OPTIONS as CIRCULATION_METHOD_OPTIONS_ORIGINAL } from './constants'
import { useInstantBundleData } from '../hooks/featureFlags.tsx'

/**
 * Decode a JWT, which provides the current user
 * and vendorId
 *
 * @param token {string} - current JWT for auth'd user
 * @returns {{vendor: object, user: object}}
 */
const decodeToken = (token) => {
  return jwt_decode(token)
}

// interface to app storage
const storage = new Storage()

const DEFAULT_EXPIRATION_TIME = 60 * 60 * 8 * 1000
const DEFAULT_METHOD = CIRCULATION_METHOD_OPTIONS_ORIGINAL.all.id

// default app settings. these are ephemeral
// settings that could change between sessions
// or even within a session.
const defaultSettings = {
  isHidingMargin: false,
  isHidingInstantLibraries: false,
  isHidingFlexLibraries: false,
  isHidingFlexCirculations: false,
  isHidingAllRevenue: false,
  isHidingFlexRevenue: false,
  isHidingInstantRevenue: false,
  isHidingInstantBundles: false,
  showInstantBundles: false,
  isHidingHolds: false,
  isMasqueradingVendor: false,
  isModelSwitchingEnabled: true,
  shouldHideFlexCirculations: false,
  shouldHideFlexRevenue: false,
  shouldHideInstantRevenue: false,
  kinds: [],
  vendor: null,
  user: null,
}

export const AppState = React.createContext(null)

export const AppStateProvider = ({ children }) => {
  const [token, setToken] = useStoredReducer(
    null,
    TOKEN_STORAGE_KEY,
    DEFAULT_EXPIRATION_TIME,
  )
  const [settings, setSettings] = useStoredReducer(
    defaultSettings,
    SETTINGS_STORAGE_KEY,
    DEFAULT_EXPIRATION_TIME,
  )
  const [publishers, setPublishers] = useSafeSetState([])
  const [purchaseMethod, setPurchaseMethod] = useStoredMethod(DEFAULT_METHOD)
  const [booting, setBooting] = useSafeSetState(token === null)
  const [errorMessages, setErrorMessages] = useSafeSetState([])
  const { vendor, user, showInstantBundles } = settings
  const isInstantBundleEnabled = useInstantBundleData()
  const CIRCULATION_METHOD_OPTIONS =
    useAvailableCirculationMethods(showInstantBundles)

  useQuery(GET_KINDS, {
    onCompleted: (data) => setSettings({ kinds: data.kinds }),
  })
  useQuery(GET_VENDOR, {
    variables: { id: user?.vendorId },
    skip: !user?.vendorId,
    onCompleted: (data) => setSettings({ vendor: data.vendor }),
    onError: (e) => {
      console.error(e)
      setErrorMessages([
        ...errorMessages,
        'There was an issue loading publisher data',
      ])
    },
  })
  useQuery(GET_VENDOR_PUBLISHERS, {
    variables: { vendorId: user?.vendorId },
    skip: !user?.vendorId,
    onCompleted: (data) => setPublishers(data.vendorPublishers),
    onError: (e) => {
      console.error(e)
      setErrorMessages([
        ...errorMessages,
        'There was an issue loading publisher data',
      ])
    },
  })

  useEffect(() => {
    if (token && token.value) {
      setSettings({ user: decodeToken(token.value) })
    } else {
      setSettings({ user: undefined })
    }
  }, [JSON.stringify(token)])

  useEffect(() => {
    if (vendor?.productTypes?.length === 1) {
      const onlyProductType =
        CIRCULATION_METHOD_OPTIONS[vendor.productTypes[0].toLowerCase()].id
      setPurchaseMethod(onlyProductType)
    }
  }, [vendor?.productTypes])

  useEffect(() => {
    const { vendor, user, kinds } = settings
    if (token && token.value) {
      if (vendor && user && kinds.length && publishers.length) setBooting(false)
      else setBooting(true)
    } else {
      setBooting(false)
    }
  }, [
    JSON.stringify(token),
    settings.vendor,
    settings.user,
    settings.kinds,
    publishers.length,
  ])

  useEffect(() => {
    const { vendor, user, isMasqueradingVendor } = settings
    /**
     * Check several layers of overrides to see if
     * a directive should be used
     *
     * @param primaryTest {boolean} - an initial condition that can short circuit this test
     * @param vendorSetting {boolean} - a vendor setting (e.g. hideFlexCircs) provided from backend
     * @returns {*|boolean}
     */
    const shouldComply = (primaryTest, vendorSetting) => {
      return (
        primaryTest &&
        ((!user?.isAdmin && vendorSetting) ||
          (user?.isAdmin && vendorSetting && isMasqueradingVendor))
      )
    }
    setSettings({
      ...settings,
      isHidingMargin: shouldComply(true, vendor?.hideMargin),
      isHidingInstantLibraries: shouldComply(
        CIRCULATION_METHOD_OPTIONS.instant.id === purchaseMethod,
        vendor?.hideInstantLibraryNames,
      ),
      isHidingFlexLibraries: shouldComply(
        CIRCULATION_METHOD_OPTIONS.flex.id === purchaseMethod,
        vendor?.hideFlexLibraryNames,
      ),
      isHidingFlexCirculations: shouldComply(
        CIRCULATION_METHOD_OPTIONS.flex.id === purchaseMethod,
        vendor?.hideFlexCircs,
      ),
      isHidingAllRevenue: shouldComply(
        CIRCULATION_METHOD_OPTIONS.all.id === purchaseMethod,
        vendor?.hideFlexRevenue && vendor?.hideInstantRevenue,
      ),
      isHidingFlexRevenue: shouldComply(
        CIRCULATION_METHOD_OPTIONS.flex.id === purchaseMethod,
        vendor?.hideFlexRevenue,
      ),
      isHidingInstantRevenue: shouldComply(
        CIRCULATION_METHOD_OPTIONS.instant.id === purchaseMethod,
        vendor?.hideInstantRevenue,
      ),
      // Instant Bundle Visibility Settings:
      // Both variables check vendor settings (vendor?.hideInstantBundles) + feature flag (isInstantBundleEnabled)
      // 
      // showInstantBundles: Should we render this component at all
      // isHidingInstantBundles: Admin-aware check for hiding instant bundle data/values
      showInstantBundles:
        Boolean(!vendor?.hideInstantBundles) && Boolean(isInstantBundleEnabled),
      isHidingInstantBundles:
        shouldComply(true, Boolean(!vendor?.hideInstantBundles)) &&
        Boolean(isInstantBundleEnabled),
      isHidingHolds: shouldComply(true, vendor?.hideHolds),
      shouldHideFlexCirculations: shouldComply(true, vendor?.hideFlexCircs),
      shouldHideFlexRevenue: shouldComply(true, vendor?.hideFlexRevenue),
      shouldHideInstantRevenue: shouldComply(true, vendor?.hideInstantRevenue),
    })
  }, [
    settings.vendor,
    settings.user,
    settings.isMasqueradingVendor,
    purchaseMethod,
  ])

  useEffect(() => {
    // Clearing out appStorage and resetting Apollo on login/logout
    storage.remove(CIRCULATIONS_OVERVIEW_FILTERS_STORAGE_KEY)
    storage.remove(CIRCULATIONS_HOLDS_FILTERS_STORAGE_KEY)
    storage.remove(GLANCE_FILTERS_STORAGE_KEY)
    storage.remove(TITLE_PERFORMANCE_FILTERS_STORAGE_KEY)
    storage.remove(TITLE_CIRCULATIONS_FILTERS_STORAGE_KEY)
    storage.remove(INVENTORY_FILTERS_STORAGE_KEY)
    client.resetStore()
  }, [token])

  useEffect(() => {
    client.resetStore()
  }, [purchaseMethod])

  /**
   * Toggle the masquerade vendor settings in
   * the app state
   *
   * @param should {boolean} - should the admin user masquerade a vendor user?
   */
  const masqueradeVendor = async (should) => {
    const { vendor, user } = settings

    if (!user || !vendor) return logout()

    if (!user?.isAdmin) throw new Error('Only admin users can do this')

    // dump apollo cache to force re-fetching everything
    await client.reFetchObservableQueries()
    return setSettings({ isMasqueradingVendor: should })
  }

  /**
   * Complete the authentication process for a provided JWT token
   *
   * @param token {string} - current JWT for auth'd user
   */
  const completeLogin = (token) => {
    if (!token) throw new Error('No token found for current user')

    setToken({ value: token })
  }

  /**
   * Complete logout process
   */
  const logout = async () => {
    setToken(null)
    setPurchaseMethod(DEFAULT_METHOD)
    setPublishers([])
    setSettings({
      ...defaultSettings,
      kinds: settings.kinds,
    })
  }

  return (
    <AppState.Provider
      value={{
        booting,
        purchaseMethod,
        setPurchaseMethod,
        settings,
        setSettings,
        completeLogin,
        logout,
        masqueradeVendor,
        publishers,
        token,
      }}
    >
      {children}
    </AppState.Provider>
  )
}
